"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { getQueryClient } from "~/widgets/lib/query-client";
import { trackConversion } from "~/widgets/lib/tracking";
import { basketStore } from "~/widgets/stores/basket-store";
import { requestBasketDrawer } from "~/widgets/lib/ensure-basket-element";
import { useBasketItemCount } from "~/widgets/lib/use-basket";
import type { BasketItem } from "~/widgets/stores/basket-store";
import { openBasketCheckout } from "~/widgets/lib/open-basket-checkout";
import { describeErrorForLog } from "~/widgets/lib/utils";
import {
  CheckoutCalendarVisibility,
  type CheckoutCalendarVisibilityProps,
} from "@baluu/ui/components/checkout/flows/checkout-calendar-visibility";
import type { ListingInfo } from "@baluu/ui/components/checkout/calendar-visibility-step";
import {
  fetchCourseWithBundles,
  fetchAvailableSlots,
  confirmFreeBooking,
  validateDiscount,
  type CheckoutApiConfig,
  type CourseWithBundlesResponse,
  type CustomerBookingHistory,
} from "@baluu/ui/lib/checkout";
import { CheckoutFlow, type StripePaymentData } from "../CheckoutFlow";
import { StripePaymentPortal } from "../StripePaymentPortal";
import { SuccessScreenPortal } from "../SuccessScreenPortal";
import { TermsDialogPortal } from "../TermsDialogPortal";
import type { TermsSettings, CustomTerms } from "@baluu/ui/components/checkout/utils/types";
import type { SuccessData } from "../PortalManager";

const STRIPE_PUBLIC_KEY =
  (typeof import.meta !== "undefined"
    ? import.meta.env?.VITE_STRIPE_PUBLIC_KEY
    : "") || "";

const DEFAULT_API_URL = "https://api.baluu.io";

export type CalendarVisibilityProps = {
  listingIds: string;
  businessId: string;
  apiUrl?: string;
  primaryColor?: string;
  theme?: "light" | "dark";
  widgetBaseUrl?: string;
  gtagConversionId?: string;
  gtagConversionLabel?: string;
  businessSlug?: string;
};

type PortalMode = "inline" | "checkout" | "stripe" | "success";

export function CalendarVisibility({
  listingIds,
  businessId,
  apiUrl,
  primaryColor = "#03c2b9",
  theme: _theme = "light",
  widgetBaseUrl,
  gtagConversionId,
  gtagConversionLabel,
  businessSlug,
}: CalendarVisibilityProps) {
  const queryClient = getQueryClient();

  const [portalMode, setPortalMode] = useState<PortalMode>("inline");
  const [stripePaymentData, setStripePaymentData] = useState<StripePaymentData | null>(null);
  const [successData, setSuccessData] = useState<SuccessData | null>(null);
  // Bumped to remount the inline checkout flow after proceeding to basket, so it returns to the calendar view
  const [checkoutResetKey, setCheckoutResetKey] = useState(0);

  // Course data state
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [listings, setListings] = useState<ListingInfo[]>([]);
  const [coursesData, setCoursesData] = useState<Map<string, any>>(new Map());
  const [ticketTypesMap, setTicketTypesMap] = useState<Map<string, any[]>>(new Map());
  const [ticketTypesV2Map, setTicketTypesV2Map] = useState<Map<string, any[]>>(new Map());
  const [priorPurchasedTicketIdsMap, setPriorPurchasedTicketIdsMap] = useState<Map<string, string[]>>(new Map());
  const [customerBookingHistoryMap, setCustomerBookingHistoryMap] = useState<Map<string, CustomerBookingHistory>>(new Map());
  const [addOnsMap, setAddOnsMap] = useState<Map<string, any[]>>(new Map());
  const [school, setSchool] = useState<any>(null);
  const [stripeKey, setStripeKey] = useState<string>(STRIPE_PUBLIC_KEY);
  const [termsSettings, setTermsSettings] = useState<TermsSettings | undefined>();
  const [customTerms, setCustomTerms] = useState<CustomTerms | undefined>();

  // Basket state

  const basketItemCount = useBasketItemCount(businessId);

  const pricingDataRef = useRef<Record<string, unknown> | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const parsedListingIds = listingIds.split(",").map((id) => id.trim()).filter(Boolean);

  const courseTimezone = listings[0]?.timezone || "Europe/London";
  const courseCurrency = listings[0]?.currency || "GBP";

  const apiConfig: CheckoutApiConfig = {
    baseUrl: apiUrl || DEFAULT_API_URL,
    source: "widget",
    isWebcomponent: true,
    onAnalytics: (event, data) => {
      if (event === "pricing_summary" && data && typeof data === "object") {
        pricingDataRef.current = data as Record<string, unknown>;
      }
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("baluu:analytics", { detail: { event, data } }),
        );
      }
    },
    onError: (err) => {
      // Message + HTTP status ONLY — never the raw error object. Same rule as
      // CheckoutFlow's apiConfig: this runs on a partner's page, and the
      // AxiosErrors the shared checkout lib forwards here carry `.config`
      // (request headers and body) on them. See `describeErrorForLog`.
      console.error("[baluu-calendar] Error:", describeErrorForLog(err));
    },
  };

  // Fetch all listing data on mount
  useEffect(() => {
    async function loadListings() {
      setIsLoading(true);
      setError(null);

      try {
        const results = await Promise.allSettled(
          parsedListingIds.map((id) => fetchCourseWithBundles(id, apiConfig)),
        );

        const newListings: ListingInfo[] = [];
        const newCoursesData = new Map<string, any>();
        const newTicketTypesMap = new Map<string, any[]>();
        const newTicketTypesV2Map = new Map<string, any[]>();
        const newPriorPurchasedTicketIdsMap = new Map<string, string[]>();
        const newCustomerBookingHistoryMap = new Map<string, CustomerBookingHistory>();
        const newAddOnsMap = new Map<string, any[]>();

        for (let i = 0; i < results.length; i++) {
          const result = results[i];
          const listingId = parsedListingIds[i]!;

          if (result!.status !== "fulfilled") continue;

          const data = (result as PromiseFulfilledResult<CourseWithBundlesResponse>).value;
          const course = data.course;

          if (!course.isAppointmentScheduleEnabled) continue;

          newCoursesData.set(listingId, course);
          newTicketTypesMap.set(listingId, data.ticketTypes || []);
          newTicketTypesV2Map.set(listingId, data.ticketTypesV2 || []);
          newPriorPurchasedTicketIdsMap.set(listingId, data.priorPurchasedTicketIds || []);
          if (data.customerBookingHistory) {
            newCustomerBookingHistoryMap.set(listingId, data.customerBookingHistory);
          }
          newAddOnsMap.set(listingId, data.allAddOns || []);

          if (!school && data.school) {
            setSchool(data.school);
          }

          if (data.stripePublicKey) {
            setStripeKey(data.stripePublicKey);
          }

          // Build terms settings from school data
          if (data.school?.settings?.termsAndConditions) {
            setTermsSettings({
              mandatoryTickBox: data.school.settings.termsAndConditions.mandatoryTickBox,
              text: data.school.settings.termsAndConditions.text,
              updated: data.school.settings.termsAndConditions.updated,
              cancellationPolicy: data.school.settings.cancellationPolicy,
              reschedulePolicy: data.school.settings.reschedulePolicy,
            } as any);
          }

          if (data.school?.customTerms) {
            setCustomTerms(data.school.customTerms as any);
          }

          // Build pricing options from ticket types
          const durationMap = new Map<string, number>();
          for (const slot of course.appointmentScheduleSlots || []) {
            for (const ticket of slot.tickets || []) {
              if (typeof ticket.duration === "number" && ticket.duration > 0) {
                for (const ttId of ticket.ticketTypes || []) {
                  if (!durationMap.has(ttId)) durationMap.set(ttId, ticket.duration);
                }
              }
            }
          }

          const pricingOptions = (data.ticketTypes || []).map((tt: any) => ({
            id: tt._id,
            name: tt.name,
            description: tt.description,
            price: tt.price,
            duration: durationMap.get(tt._id) ?? tt.duration ?? 60,
            ticketsIds: tt.ticketsIds || [tt._id],
          }));

          const lowestPrice = pricingOptions.length > 0
            ? Math.min(...pricingOptions.map((o: any) => o.price))
            : course.appointmentScheduleBriefing?.price || 0;

          newListings.push({
            id: listingId,
            title: course.title,
            description: course.summary?.description,
            thumbnail: course.images?.[0]?.url || course.thumbnail,
            price: lowestPrice,
            currency: course.currency || "GBP",
            isOnline: course.isOnline,
            pricingOptions,
            appointmentScheduleSlots: course.appointmentScheduleSlots,
            timezone: course.timezone,
          });
        }

        if (newListings.length === 0) {
          setError("No appointment listings found.");
        }

        setListings(newListings);
        setCoursesData(newCoursesData);
        setTicketTypesMap(newTicketTypesMap);
        setTicketTypesV2Map(newTicketTypesV2Map);
        setPriorPurchasedTicketIdsMap(newPriorPurchasedTicketIdsMap);
        setCustomerBookingHistoryMap(newCustomerBookingHistoryMap);
        setAddOnsMap(newAddOnsMap);
      } catch (err) {
        setError("Failed to load listings.");
        console.error("[baluu-calendar] Failed to load listings:", err);
      } finally {
        setIsLoading(false);
      }
    }

    if (parsedListingIds.length > 0) {
      loadListings();
    }
  }, [listingIds]);

  // API callbacks
  const handleLoadAvailableSlots = useCallback(
    async (
      listingId: string,
      month: number,
      year: number,
      ticketTypeId?: string,
    ) => {
      // Forwarded so the server can attach each slot's resource-pool cap. This
      // call dropped the ticket type entirely, so the cap could never be
      // computed for this widget regardless of any client-side wiring.
      // NOTE the undefined: bookingType is the 5th parameter, ticketTypeId the
      // 6th. Passing it positionally in the wrong slot would send the ticket id
      // as a booking type and silently compute nothing.
      return fetchAvailableSlots(
        listingId,
        month,
        year,
        apiConfig,
        undefined,
        ticketTypeId,
      );
    },
    [apiConfig.baseUrl],
  );

  const handleConfirmFreeBooking = useCallback(
    async (data: any) => {
      await confirmFreeBooking(data, apiConfig, { processImmediately: true });
    },
    [apiConfig.baseUrl],
  );

  const handleValidateDiscount = useCallback(
    async (code: string, email: string) => {
      const courseId = listings[0]?.id || "";
      return validateDiscount(code, email, courseId, apiConfig, {
        schoolId: school?._id,
      });
    },
    [apiConfig.baseUrl, listings, school],
  );

  const handleStripePaymentSuccess = useCallback((data: SuccessData) => {
    setSuccessData(data);
    setPortalMode("success");

    const detail = {
      businessId,
      ...data,
      grossTotal: stripePaymentData?.grossTotal,
      netTotal: stripePaymentData?.netTotal,
      discountAmount: stripePaymentData?.discountAmount,
      discountType: stripePaymentData?.discountType,
    };

    document.dispatchEvent(
      new CustomEvent("baluu:booking-completed", { bubbles: true, composed: true, detail }),
    );
    trackConversion("baluu_booking_completed", detail, gtagConversionId && gtagConversionLabel
      ? { conversionId: gtagConversionId, conversionLabel: gtagConversionLabel }
      : undefined,
    );
  }, [businessId, stripePaymentData, gtagConversionId, gtagConversionLabel]);

  const handleStripeClose = useCallback(() => {
    setPortalMode("checkout");
    setStripePaymentData(null);
  }, []);

  const handleSuccessClose = useCallback(() => {
    setPortalMode("inline");
    setSuccessData(null);
  }, []);

  const handleSuccess = useCallback((purchase: any) => {
    const pricing = pricingDataRef.current;
    const detail = {
      businessId,
      ...(pricing && {
        grossTotal: pricing.grossTotal as number | undefined,
        netTotal: pricing.netTotal as number | undefined,
        discountAmount: pricing.discountAmount as number | undefined,
        discountType: pricing.discountType as string | undefined,
      }),
    };

    document.dispatchEvent(
      new CustomEvent("baluu:booking-completed", { bubbles: true, composed: true, detail }),
    );
    trackConversion("baluu_booking_completed", detail, gtagConversionId && gtagConversionLabel
      ? { conversionId: gtagConversionId, conversionLabel: gtagConversionLabel }
      : undefined,
    );

    pricingDataRef.current = null;
  }, [businessId, gtagConversionId, gtagConversionLabel]);

  const handleCheckoutStart = useCallback(() => {
    setPortalMode("checkout");
  }, []);

  const handleCheckoutEnd = useCallback(() => {
    setPortalMode("inline");
  }, []);

  const handleClose = useCallback(() => {
    setPortalMode("inline");
  }, []);

  // Basket callbacks
  const handleAddToBasket = useCallback(
    (itemData: any) => {
      const item: Omit<BasketItem, "id" | "addedAt"> = {
        type: itemData.type || "booking",
        schoolId: businessId,
        schoolName: school?.name || businessId,
        courseId: itemData.courseId,
        courseName: itemData.courseName,
        sessionId: itemData.sessionId,
        sessionDate: itemData.sessionDate,
        // The LISTING's clock. Every one of these handlers copies fields
        // one by one, so a field the flows set is silently dropped unless
        // it is named here -- which is what happened to this one.
        timezone: itemData.timezone,
        ticketSelections: itemData.ticketSelections,
        addOns: itemData.addOns,
        bookingType: itemData.bookingType,
        isOnline: itemData.isOnline,
        appointmentData: itemData.appointmentData,
        userInputs: itemData.userInputs,
        // Attendees from the participants step. Dropped here until Oct 2026,
        // so embedded-calendar bookings on attendee-collecting listings paid
        // without any attendee data (see CheckoutOverlay, which copies it).
        participants: itemData.participants,
        quantity: 1,
        price: itemData.price,
        currency: itemData.currency || courseCurrency,
        imageUrl: itemData.imageUrl,
      };
      basketStore.addItem(item);
      handleClose();
      requestBasketDrawer(businessId);
    },
    [businessId, school, courseCurrency, handleClose],
  );

  // Ephemeral single-item flow: clear any prior items for this school, add the new one, open basket checkout
  const handleProceedToBasketCheckout = useCallback(
    (itemData: any) => {
      const item: Omit<BasketItem, "id" | "addedAt"> = {
        type: itemData.type || "booking",
        schoolId: businessId,
        schoolName: school?.name || businessId,
        courseId: itemData.courseId,
        courseName: itemData.courseName,
        sessionId: itemData.sessionId,
        sessionDate: itemData.sessionDate,
        // The LISTING's clock. Every one of these handlers copies fields
        // one by one, so a field the flows set is silently dropped unless
        // it is named here -- which is what happened to this one.
        timezone: itemData.timezone,
        ticketSelections: itemData.ticketSelections,
        addOns: itemData.addOns,
        bookingType: itemData.bookingType,
        isOnline: itemData.isOnline,
        appointmentData: itemData.appointmentData,
        userInputs: itemData.userInputs,
        // Attendees from the participants step. Dropped here until Oct 2026,
        // so embedded-calendar bookings on attendee-collecting listings paid
        // without any attendee data (see CheckoutOverlay, which copies it).
        participants: itemData.participants,
        quantity: 1,
        price: itemData.price,
        currency: itemData.currency || courseCurrency,
        imageUrl: itemData.imageUrl,
      };
      basketStore.clearBasket(businessId);
      basketStore.addItem(item);
      handleClose();
      openBasketCheckout({ businessId, apiUrl, primaryColor });
      setCheckoutResetKey((k) => k + 1);
    },
    [businessId, school, courseCurrency, apiUrl, primaryColor, handleClose],
  );

  // Terms dialog state
  const [termsDialogData, setTermsDialogData] = useState<{
    isOpen: boolean;
    termsSettings?: TermsSettings;
    customTerms?: CustomTerms;
    timezone?: string;
    ticketPolicies?: any[];
  }>({ isOpen: false });

  const handleOpenTermsDialog = useCallback((data: any) => {
    setTermsDialogData({ isOpen: true, ...data });
  }, []);

  const handleCloseTermsDialog = useCallback(() => {
    setTermsDialogData((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const handleStepChange = useCallback(() => {
    setTimeout(() => {
      scrollContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    }, 50);
  }, []);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground" />
        <span className="ml-3 text-muted-foreground">Loading availability...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      {/* Inline calendar + overlay checkout */}
      <div ref={scrollContainerRef}>
        <CheckoutCalendarVisibility
          key={checkoutResetKey}
          listings={listings}
          coursesData={coursesData}
          ticketTypesMap={ticketTypesMap}
          ticketTypesV2Map={ticketTypesV2Map}
          priorPurchasedTicketIdsMap={priorPurchasedTicketIdsMap}
          customerBookingHistoryMap={customerBookingHistoryMap}
          addOnsMap={addOnsMap}
          timezone={courseTimezone}
          currency={courseCurrency}
          onLoadAvailableSlots={handleLoadAvailableSlots}
          onConfirmFreeBooking={handleConfirmFreeBooking}
          onValidateDiscount={handleValidateDiscount}
          onSuccess={handleSuccess}
          onError={(err) =>
            console.error("[baluu-calendar] Checkout error:", describeErrorForLog(err))
          }
          onClose={handleClose}
          onStepChange={handleStepChange}
          onCheckoutStart={handleCheckoutStart}
          onCheckoutEnd={handleCheckoutEnd}
          cancellationPolicyUrl={school?.cancellationPolicyUrl}
          termsSettings={termsSettings}
          customTerms={customTerms}
          onOpenTermsDialog={handleOpenTermsDialog}
          school={school}
          mandatoryTickBox={school?.settings?.termsAndConditions?.mandatoryTickBox}
          isEmbedMode={false}
          basketItemCount={basketItemCount}
          onAddToBasket={undefined}
          onProceedToBasketCheckout={handleProceedToBasketCheckout}
          directCheckout={true}
        />
      </div>

      {/* Stripe Payment Portal */}
      {stripePaymentData && (
        <StripePaymentPortal
          isOpen={portalMode === "stripe"}
          paymentData={stripePaymentData}
          stripePublicKey={stripePaymentData.stripePublicKey || stripeKey}
          primaryColor={primaryColor}
          onSuccess={handleStripePaymentSuccess}
          onClose={handleStripeClose}
        />
      )}

      {/* Success Screen Portal */}
      {successData && (
        <SuccessScreenPortal
          isOpen={portalMode === "success"}
          successData={successData}
          primaryColor={primaryColor}
          onClose={handleSuccessClose}
        />
      )}

      {/* Terms Dialog Portal */}
      <TermsDialogPortal
        isOpen={termsDialogData.isOpen}
        onClose={handleCloseTermsDialog}
        settings={termsDialogData.termsSettings}
        customTerms={termsDialogData.customTerms}
        primaryColor={primaryColor}
        ticketPolicies={termsDialogData.ticketPolicies}
      />
    </QueryClientProvider>
  );
}
